import { useState } from "react";
import { api, adminAuth } from "../../lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

const COLOR_PALETTE = [
  { name: "White", hex: "#FFFFFF" },
  { name: "Black", hex: "#1A1A1A" },
  { name: "Grey", hex: "#808080" },
  { name: "Brown", hex: "#5C4033" },
  { name: "Red", hex: "#C0392B" },
  { name: "Blue", hex: "#2E5A88" },
  { name: "Navy", hex: "#1B2A41" },
  { name: "Bone", hex: "#F4F1EA" },
  { name: "Gold", hex: "#C59B6D" },
  { name: "Forest", hex: "#2E4D3A" },
];

const SIZE_PRESETS = {
  tshirt: ["S", "M", "L", "XL", "2X", "3X", "4X"],
  tumbler: ["20oz", "30oz", "40oz", "32oz"],
  mug: ["11oz", "15oz"],
  handmade: ["One Size", "Small", "Medium", "Large"],
  other: ["One Size"],
};

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--navy)] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export default function AddProductTab({ products = [], onCreated, goToProducts }) {
  const templateFor = (cat) => {
    const src = products.find((p) => p.category === cat);
    return {
      sizes: src?.sizes?.length ? [...src.sizes] : (SIZE_PRESETS[cat] ? SIZE_PRESETS[cat].slice(0, 4) : []),
      colors: src?.colors?.length ? src.colors.map((c) => c.name || c) : ["White", "Black"],
      price: src?.base_price != null ? String(src.base_price) : "",
    };
  };
  const initTemplate = templateFor("tshirt");
  const [form, setForm] = useState({
    name: "", category: "tshirt", base_price: initTemplate.price, description: "",
    stock_count: "100", low_stock_threshold: "5", shipping_fee: "6.99",
  });
  const [colors, setColors] = useState(initTemplate.colors);
  const [sizes, setSizes] = useState(initTemplate.sizes);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [copyFrom, setCopyFrom] = useState("");
  const [customizable, setCustomizable] = useState(true);
  const [excludeDiscounts, setExcludeDiscounts] = useState(false);
  const [excludeFreeShipping, setExcludeFreeShipping] = useState(false);
  const [saving, setSaving] = useState(false);

  const presetSources = products.filter((p) => (p.preset_designs?.length || 0) > 0);
  const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleColor = (name) => setColors((c) => c.includes(name) ? c.filter((x) => x !== name) : [...c, name]);
  const toggleSize = (s) => setSizes((arr) => arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s]);

  const onCategory = (e) => {
    const cat = e.target.value;
    const t = templateFor(cat);
    setForm({ ...form, category: cat, base_price: t.price });
    setSizes(t.sizes);
    setColors(t.colors);
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Image too large. Max 15MB."); return; }
    setPhoto(f);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const price = parseFloat(form.base_price);
    if (isNaN(price) || price <= 0) { toast.error("Enter a valid price"); return; }
    if (!photo) { toast.error("Upload a product photo"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        description: form.description.trim(),
        base_price: price,
        shipping_fee: form.shipping_fee === "" ? null : (parseFloat(form.shipping_fee) || 0),
        colors: COLOR_PALETTE.filter((c) => colors.includes(c.name)),
        sizes,
        stock_count: parseInt(form.stock_count) || 0,
        low_stock_threshold: parseInt(form.low_stock_threshold) || 0,
        customizable,
        exclude_from_discounts: excludeDiscounts,
        exclude_from_free_shipping: excludeFreeShipping,
      };
      if (customizable && copyFrom) {
        const src = products.find((p) => p.id === copyFrom);
        if (src?.preset_designs?.length) {
          payload.preset_designs = src.preset_designs.map((d) => ({ id: newId(), name: d.name, image_url: d.image_url }));
        }
      }
      const r = await api.post("/admin/products", payload, { headers: adminAuth() });
      const pid = r.data.id;
      const fd = new FormData();
      fd.append("file", photo);
      await api.post(`/admin/products/${pid}/image`, fd, { headers: { ...adminAuth(), "Content-Type": "multipart/form-data" } });
      toast.success(`"${payload.name}" added`);
      await onCreated?.();
      goToProducts?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-rustic" data-testid="admin-add-product">
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]">
        <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--crimson)]">/ADD PRODUCT</div>
        <div className="font-serif-display text-2xl text-[var(--navy)]">New Product</div>
      </div>
      <form onSubmit={submit} className="p-6 grid lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <Labeled label="Product name">
            <input className="input-field" value={form.name} onChange={set("name")} placeholder="e.g. Vintage Pocket Tee" data-testid="add-product-name"/>
          </Labeled>
          <div className="grid grid-cols-3 gap-4">
            <Labeled label="Category">
              <select className="input-field" value={form.category} onChange={onCategory} data-testid="add-product-category">
                <option value="tshirt">T-Shirt</option>
                <option value="tumbler">Tumbler</option>
                <option value="mug">Mug</option>
                <option value="handmade">Hand Made</option>
                <option value="other">Other</option>
              </select>
            </Labeled>
            <Labeled label="Price ($)">
              <input className="input-field" type="number" step="0.01" value={form.base_price} onChange={set("base_price")} placeholder="20.00" data-testid="add-product-price"/>
            </Labeled>
            <Labeled label="Shipping ($)">
              <input className="input-field" type="number" step="0.01" value={form.shipping_fee} onChange={set("shipping_fee")} placeholder="6.99" data-testid="add-product-shipping"/>
            </Labeled>
          </div>
          <Labeled label="Description">
            <textarea className="input-field resize-y" rows={3} value={form.description} onChange={set("description")} placeholder="A short, friendly description…" data-testid="add-product-description"/>
          </Labeled>
          <div className="grid grid-cols-2 gap-4">
            <Labeled label="Stock count">
              <input className="input-field" type="number" value={form.stock_count} onChange={set("stock_count")} data-testid="add-product-stock"/>
            </Labeled>
            <Labeled label="Low-stock alert at">
              <input className="input-field" type="number" value={form.low_stock_threshold} onChange={set("low_stock_threshold")} data-testid="add-product-threshold"/>
            </Labeled>
          </div>
        </div>

        <div className="space-y-5">
          <Labeled label="Photo">
            <label className="block border-2 border-dashed border-[rgba(27,42,65,0.25)] hover:border-[var(--gold)] rounded-sm p-6 text-center cursor-pointer transition" data-testid="add-product-photo-label">
              {photoPreview ? (
                <img src={photoPreview} alt="" className="mx-auto max-h-40 object-contain rounded-sm"/>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-[var(--navy)] mx-auto mb-2"/>
                  <div className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[var(--navy)]">Upload product photo</div>
                  <div className="text-xs text-[var(--ink-soft)] mt-1">PNG, JPG or WEBP · up to 15MB</div>
                </>
              )}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPhoto} data-testid="add-product-photo-input"/>
            </label>
          </Labeled>

          <Labeled label={`Colors (${colors.length})`}>
            <div className="flex flex-wrap gap-2">
              {COLOR_PALETTE.map((c) => (
                <button
                  type="button"
                  key={c.name}
                  onClick={() => toggleColor(c.name)}
                  data-testid={`add-product-color-${c.name.toLowerCase()}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono-ui transition ${colors.includes(c.name) ? "border-[var(--gold)] bg-[var(--navy)] text-[var(--bone)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)]"}`}
                >
                  <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ backgroundColor: c.hex }}/>
                  {c.name}
                </button>
              ))}
            </div>
          </Labeled>

          <Labeled label={`Sizes (${sizes.length})`}>
            <div className="flex flex-wrap gap-2">
              {(SIZE_PRESETS[form.category] || []).map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleSize(s)}
                  data-testid={`add-product-size-${s.toLowerCase()}`}
                  className={`min-w-[3rem] px-3 py-2 rounded-sm border font-mono-ui text-xs tracking-[0.1em] transition ${sizes.includes(s) ? "bg-[var(--navy)] text-[var(--bone)] border-[var(--navy)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)]"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Labeled>

          <Labeled label="Customization">
            <button
              type="button"
              onClick={() => setCustomizable((v) => !v)}
              data-testid="add-product-customizable-toggle"
              className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border transition ${customizable ? "border-[var(--gold)] bg-[var(--navy)] text-[var(--bone)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)]"}`}
            >
              <span className="font-mono-ui text-xs tracking-[0.12em]">
                {customizable ? "Customizable (presets / text / upload)" : "Sold as-is — no customization"}
              </span>
              <span className={`w-10 h-5 rounded-full relative transition ${customizable ? "bg-[var(--gold)]" : "bg-[rgba(27,42,65,0.25)]"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${customizable ? "left-[22px]" : "left-0.5"}`}/>
              </span>
            </button>
            <div className="mt-1.5 text-xs text-[var(--ink-soft)]">
              Turn off for items customers buy as shown (no design options on the product page).
            </div>
          </Labeled>

          <Labeled label="Promotions">
            <div className="grid gap-2">
              <button
                type="button"
                onClick={() => setExcludeDiscounts((v) => !v)}
                data-testid="add-product-exclude-discounts"
                className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border transition ${excludeDiscounts ? "border-[var(--crimson)] bg-[var(--crimson)]/10 text-[var(--navy)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)]"}`}
              >
                <span className="font-mono-ui text-xs tracking-[0.12em]">Exclude from discounts</span>
                <span className={`w-10 h-5 rounded-full relative transition ${excludeDiscounts ? "bg-[var(--crimson)]" : "bg-[rgba(27,42,65,0.25)]"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${excludeDiscounts ? "left-[22px]" : "left-0.5"}`}/>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setExcludeFreeShipping((v) => !v)}
                data-testid="add-product-exclude-free-shipping"
                className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border transition ${excludeFreeShipping ? "border-[var(--crimson)] bg-[var(--crimson)]/10 text-[var(--navy)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)]"}`}
              >
                <span className="font-mono-ui text-xs tracking-[0.12em]">Exclude from free shipping</span>
                <span className={`w-10 h-5 rounded-full relative transition ${excludeFreeShipping ? "bg-[var(--crimson)]" : "bg-[rgba(27,42,65,0.25)]"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${excludeFreeShipping ? "left-[22px]" : "left-0.5"}`}/>
                </span>
              </button>
            </div>
            <div className="mt-1.5 text-xs text-[var(--ink-soft)]">
              Discount codes skip excluded items; excluded items always pay their own shipping (except on local pickup).
            </div>
          </Labeled>

          {customizable && (
          <Labeled label="Use presets from another product (optional)">
            <select className="input-field" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} data-testid="add-product-copy-presets">
              <option value="">— Start with no presets —</option>
              {presetSources.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.category} ({p.preset_designs.length} presets)
                </option>
              ))}
            </select>
            {copyFrom && (
              <div className="mt-2 text-xs text-[var(--ink-soft)]" data-testid="add-product-copy-presets-note">
                {products.find((p) => p.id === copyFrom)?.preset_designs?.length || 0} preset design(s) will be copied to this product.
              </div>
            )}
          </Labeled>
          )}
        </div>

        <div className="lg:col-span-2 flex justify-end pt-2 border-t border-[rgba(27,42,65,0.08)]">
          <button type="submit" disabled={saving} className="btn-primary btn-crimson justify-center px-8" data-testid="add-product-submit">
            {saving ? "Saving…" : "Add Product"}
          </button>
        </div>
      </form>
    </div>
  );
}
